import { useEffect, useState, useMemo } from 'react';
import { X, Flag, ChevronDown } from 'lucide-react';
import { createPortal } from 'react-dom';
import { frameOrderApi } from '../../services/frameOrderApi';
import type { FollowUpEntry, FrameOrderFilters } from '../../types/frameOrders';

interface FollowUpModalProps {
    isOpen: boolean;
    onClose: () => void;
    filters: FrameOrderFilters;
    onNavigateToRow: (documentId: string) => void;
}

export function FollowUpModal({ isOpen, onClose, filters, onNavigateToRow }: FollowUpModalProps) {
    const [allFollowUps, setAllFollowUps] = useState<FollowUpEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [assigneeFilter, setAssigneeFilter] = useState('');

    useEffect(() => {
        if (!isOpen) return;

        setLoading(true);
        setError(null);
        setAssigneeFilter('');

        frameOrderApi.getFollowUps(filters)
            .then(data => setAllFollowUps(data))
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [isOpen, filters]);

    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    const availableAssignees = useMemo(() => {
        const set = new Set<string>();
        for (const fu of allFollowUps) {
            if (fu.Assignee) set.add(fu.Assignee);
        }
        return Array.from(set).sort((a, b) => a.localeCompare(b, 'de'));
    }, [allFollowUps]);

    const filteredFollowUps = useMemo(() => {
        if (!assigneeFilter) return allFollowUps;
        return allFollowUps.filter(fu => fu.Assignee === assigneeFilter);
    }, [allFollowUps, assigneeFilter]);

    if (!isOpen) return null;

    const handleItemClick = (documentId: string) => {
        onNavigateToRow(documentId);
        onClose();
    };

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />

            <div className="relative z-10 mx-4 flex max-h-[80vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                    <div className="flex items-center gap-2">
                        <Flag className="h-5 w-5 text-orange-500" />
                        <h2 className="text-lg font-semibold text-gray-900">
                            Follow-ups ({filteredFollowUps.length})
                        </h2>
                    </div>
                    <div className="flex items-center gap-2">
                        {availableAssignees.length > 1 && (
                            <div className="relative">
                                <select
                                    value={assigneeFilter}
                                    onChange={(e) => setAssigneeFilter(e.target.value)}
                                    className="h-8 appearance-none rounded-md border border-gray-300 bg-white pl-2 pr-7 text-xs text-gray-700 focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
                                >
                                    <option value="">All assignees</option>
                                    {availableAssignees.map(name => (
                                        <option key={name} value={name}>{name}</option>
                                    ))}
                                </select>
                                <ChevronDown className="pointer-events-none absolute right-2 top-2 h-3.5 w-3.5 text-gray-400" />
                            </div>
                        )}
                        <button
                            onClick={onClose}
                            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {loading && (
                        <div className="flex items-center justify-center py-8">
                            <div className="h-6 w-6 animate-spin rounded-full border-2 border-orange-500 border-t-transparent" />
                        </div>
                    )}

                    {error && (
                        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                            {error}
                        </div>
                    )}

                    {!loading && !error && filteredFollowUps.length === 0 && (
                        <p className="py-8 text-center text-sm text-gray-400">
                            No follow-ups for the current filter selection.
                        </p>
                    )}

                    {!loading && !error && filteredFollowUps.length > 0 && (
                        <div className="space-y-2">
                            {filteredFollowUps.map(fu => (
                                <button
                                    key={fu.Id}
                                    onClick={() => handleItemClick(fu.DocumentID)}
                                    className="w-full rounded-lg border border-gray-200 px-4 py-3 text-left transition-colors hover:border-orange-200 hover:bg-orange-50/50"
                                >
                                    <div className="flex items-center gap-2 text-sm">
                                        <span className="font-medium text-gray-900">{fu.DocumentNr}</span>
                                        <span className="text-xs text-gray-400">{fu.CompanyCode}</span>
                                    </div>
                                    <div className="mt-0.5 text-sm text-gray-700">{fu.CustomerName}</div>
                                    <div className="mt-0.5 text-xs text-gray-500 truncate">{fu.ProductName}</div>
                                    <div className="mt-1 flex items-center gap-2 text-xs text-gray-400">
                                        <span>{fu.CreatedBy} &middot; {new Date(fu.CreatedAt).toLocaleString('de-CH')}</span>
                                        {fu.Assignee && (
                                            <span className="rounded bg-orange-50 px-1.5 py-0.5 text-[10px] font-medium text-orange-600">
                                                {fu.Assignee}
                                            </span>
                                        )}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}
